/* eslint-disable max-lines, no-param-reassign, max-params, max-lines-per-function */
import { api } from "jiffy-api";
import { track } from "jiffy-analytics";
import { camelize, objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";
import { LOCAL_DELIVERY_CATEGORIES } from "common/shipping/constants";

const initialState = {
  changeCartItemStartTime: 0,
  changeDeliveryDateMixpanelData: {
    earliestExpectedDeliveryDate: null,
    latestExpectedDeliveryDate: null,
    previousEarliestDate: null,
    previousLatestDate: null,
    earliestTimeToGetDate: null,
    earliestTimeToRenderDate: null,
    cartTimeToGetDate: null,
    cartTimeToRenderDate: null
  },
  delivery: {
    deliveryDates: {
      mixed: [],
      mixedFastest: []
    },
    sortedShipments: [],
    subOrdersDeliveryDates: {},
    changedDeliveryDates: null,
    deliveryDatesChanged: false,
    assignmentUid: null,
    isLongWait: false
  },
  deliveryLoading: false,
  deliveryLoadingCounter: 0,
  deliveryNotification: {
    mixed: {
      deliveryDate: null,
      isPreOrder: false,
      isMultipleDeliveries: false,
      isShippingToday: false
    },
    mixedFastest: {
      deliveryDate: null,
      isPreOrder: false,
      isMultipleDeliveries: false,
      isShippingToday: false
    }
  },
  initialDeliveryDateMixpanelData: {
    isSent: false,
    earliestTimeToGetDate: null,
    earliestTimeToRenderDate: null,
    cartTimeToGetDate: null,
    cartTimeToRenderDate: null
  },
  isEligibleForBlanksFreeShipping: false,
  saleImprovedShippingPriceDetails: {},
  shipMethodChangeGroupId: null,
  shipments: {
    groupIds: [],
    orders: {},
    titles: {},
    totalShipping: {},
    totalSurcharges: {}
  },
  shippingInfo: {
    freeShippingOver: null,
    saleShippingChargeShown: false,
    shippingSaleEndDate: null,
    underShippingPrice: null
  },
  showBlanksFreeShippingBanner: false,
  subOrdersFreeShipping: {},
  shippingTexts: {},
  assignmentStuckTimers: [],
  assignmentTrackingStartTime: null
};

const shippingModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  mutations: {
    init(state, data) {
      const newState = objNamesToCamelCase(data);

      state.delivery = newState.delivery || state.delivery;
      state.deliveryNotification = newState.deliveryNotification || state.deliveryNotification;
      state.isEligibleForBlanksFreeShipping =
        newState.isEligibleForBlanksFreeShipping ?? state.isEligibleForBlanksFreeShipping;
      state.saleImprovedShippingPriceDetails =
        newState.saleImprovedShippingPriceDetails || state.saleImprovedShippingPriceDetails;
      state.shipments = newState.shipments || state.shipments;
      state.shippingInfo = newState.shippingInfo || state.shippingInfo;
      state.shippingTexts = newState.cartConfig?.shippingTexts || state.shippingTexts;
      state.showBlanksFreeShippingBanner =
        newState.showBlanksFreeShippingBanner ?? state.showBlanksFreeShippingBanner;
      state.subOrdersFreeShipping = newState.subOrdersFreeShipping || state.subOrdersFreeShipping;
    },
    setDeliveryLoadingStatus(state, status) {
      if (status) {
        state.deliveryLoadingCounter += 1;
      } else {
        state.deliveryLoadingCounter = Math.max(0, state.deliveryLoadingCounter - 1);
      }
    },
    setSelectedShipmentOption(state, { category, groupId }) {
      // Check if shipments and orders exist before proceeding
      if (!state.shipments || !state.shipments.orders || !state.shipments.orders[groupId]) {
        return;
      }

      const groupShipments = state.shipments.orders[groupId];
      let selectedOption;

      groupShipments.options = groupShipments.options.map(option => {
        if (option.category === category || camelize(option.category) === category) {
          selectedOption = option;
          option.selected = true;
        } else {
          option.selected = false;
        }
        return option;
      });

      // Standard orders would have already set the selected option from above.
      // Though for MCU orders, the selected shipping category could not be available.
      // Example:
      //   1. Selecting 'quick' but 'quick' is only avaulable for Port & Company FG.
      //   2. Selecting 'economy' but FG orders doesn't offer the 'economy' option.
      // As the 'fastest' option is always available for all orders, standard and FG orders,
      // If the customer selected a shipping category other than 'fastest',
      // then the customer is most probably considering cost over delivery dates.
      // Hence, we offer the cheapest MCU shipping option possible.
      if (!selectedOption) {
        const lastOption = groupShipments.options[groupShipments.options.length - 1];
        lastOption.selected = true;
      }
    },
    setShipMethodChangeGroupId(state, { groupId }) {
      state.shipMethodChangeGroupId = groupId;
    },
    setInitialDeliveryDateMixpanelData(state, mixpanelPartialData) {
      state.initialDeliveryDateMixpanelData = {
        ...state.initialDeliveryDateMixpanelData,
        ...mixpanelPartialData
      };
    },
    setChangeDeliveryDateMixpanelData(state, mixpanelPartialData) {
      state.changeDeliveryDateMixpanelData = {
        ...state.changeDeliveryDateMixpanelData,
        ...mixpanelPartialData
      };
    },
    setChangeCartItemStartTime(state, value) {
      state.changeCartItemStartTime = value === undefined ? performance?.now() || 0 : value;
    },
    setAssignmentStuckTimers(state, timers) {
      state.assignmentStuckTimers = timers;
    },
    setAssignmentTrackingStartTime(state, time) {
      state.assignmentTrackingStartTime = time;
    }
  },

  actions: {
    getAllDeliveryDates({ commit, dispatch }) {
      dispatch("prepareToAjax", null, { root: true });
      dispatch("startAssignmentStuckTracking");

      const cacheBuster = new Date().getTime();
      const requestStartTime = performance?.now() || 0;
      return api.vueCart
        .delivery({ cb: cacheBuster })
        .then(response => {
          const requestEndTime = performance?.now() || 0;
          const requestTotalTime = requestEndTime - requestStartTime;
          commit("setInitialDeliveryDateMixpanelData", {
            earliestTimeToGetDate: requestTotalTime,
            cartTimeToGetDate: requestTotalTime
          });
          commit("setAssignmentDuration", requestTotalTime, { root: true });
          commit("setChangeDeliveryDateMixpanelData", {
            earliestTimeToGetDate: requestTotalTime,
            cartTimeToGetDate: requestTotalTime
          });
          dispatch("setCartResponse", response, { root: true });

          // Stop assignment stuck tracking if we got data
          if (response && response.status !== 204) {
            dispatch("stopAssignmentStuckTracking");
          }
        })
        .catch(error => {
          // Stop tracking on error
          dispatch("stopAssignmentStuckTracking");
          throw error;
        })
        .finally(() => {
          commit("setDeliveryLoadingStatus", false);
        });
    },
    getInitialDeliveryDates({ commit, dispatch }) {
      dispatch("prepareToAjax", null, { root: true });
      dispatch("startAssignmentStuckTracking");

      const requestStartTime =
        window?.jiffy?.timers?.cartRenderStartTime || performance?.now() || 0;
      const fallback = () => dispatch("getAllDeliveryDates");
      const initialStatePromise =
        // This global may contain the data with which we may hidrate state
        window?.jiffy?.cart?.initialDeliveryDatesPromise?.catch(() => fallback()) || fallback();
      return initialStatePromise
        .then(response => {
          const requestEndTime = performance?.now() || 0;
          const requestTotalTime = requestEndTime - requestStartTime;
          commit("setInitialDeliveryDateMixpanelData", {
            earliestTimeToGetDate: requestTotalTime,
            cartTimeToGetDate: requestTotalTime
          });
          commit("setAssignmentDuration", requestTotalTime, { root: true });
          dispatch("setCartResponse", response, { root: true });

          // Stop assignment stuck tracking if we got data
          if (response && response.status !== 204) {
            dispatch("stopAssignmentStuckTracking");
          }
        })
        .catch(error => {
          // Stop tracking on error
          dispatch("stopAssignmentStuckTracking");
          throw error;
        });
    },
    changeShippingOptions({ commit, dispatch, getters }, category) {
      const { shipments } = getters;
      if (!shipments || !shipments.groupIds) {
        return;
      }

      shipments.groupIds.map(groupId => commit("setSelectedShipmentOption", { category, groupId }));
      dispatch("persistSelectedShipments");
    },
    changeShippingOption({ commit, dispatch, getters, rootGetters }, { category, groupId }) {
      commit("setSelectedShipmentOption", { category, groupId });
      commit("setShipMethodChangeGroupId", { groupId });
      dispatch("persistSelectedShipments", { skipLoadingUpdate: true });

      const isLocalDelivery = LOCAL_DELIVERY_CATEGORIES.has(category);
      // ignore "Design Services" as it's not a true shipment
      const { shipments } = getters;
      const shipmentGroupIds = shipments && shipments.groupIds ? shipments.groupIds : [];
      const shipmentGroups = shipmentGroupIds.filter(id => id !== "285");
      const hasSingleGroup = shipmentGroups.length === 1;
      const hasFreeShippingCoupon = rootGetters.couponCode?.freeShipping;

      if (isLocalDelivery && hasSingleGroup && hasFreeShippingCoupon) {
        dispatch("removeCoupon", "coupon_not_eligible_for_selected_shipping", { root: true });
      }
    },
    persistSelectedShipments({ dispatch, getters, rootGetters }, options = {}) {
      const { selectedShipments } = getters;

      // Don't make API call if there are no selected shipments
      if (!selectedShipments || Object.keys(selectedShipments).length === 0) {
        return Promise.resolve();
      }

      const payload = {
        delivery_option_ids: Object.values(selectedShipments).map(shipment => shipment.id)
      };

      return api.jiffyshirts.orders
        .setShippingSpeed({ order_number: rootGetters.orderNumber }, payload)
        .then(response => {
          dispatch("setCartResponse", { ...response, ...options }, { root: true });
        })
        .catch(e => {
          dispatch("setSystemError", e, { root: true });
        });
    },
    sendDeliveryDateMixpanelData({ rootState }) {
      const { initialDeliveryDateMixpanelData } = rootState.shipping;
      const { isSent = false, ...eventProps } = initialDeliveryDateMixpanelData || {};
      if (isSent) return;

      // When the data is gathered, fire the event
      const isDataComplete = Object.values(eventProps).every(value => value !== null);
      if (isDataComplete) {
        // track("delivery_dates_rendered", {
        //   page: rootGetters.isMcuCart ? "mcu-cart" : "cart",
        //   earliest_time_to_get_date: Math.floor(eventProps?.earliestTimeToGetDate),
        //   earliest_time_to_render_date: Math.floor(eventProps?.earliestTimeToRenderDate),
        //   cart_time_to_get_date: Math.floor(eventProps?.cartTimeToGetDate),
        //   cart_time_to_render_date: Math.floor(eventProps?.cartTimeToRenderDate),
        //   shipments: state?.shipments?.groupIds?.length || 0
        // });
        // commit("setInitialDeliveryDateMixpanelData", { isSent: true });
        // TODO: cleanup code related to delivery_dates_rendered calculations
        // Cart: https://github.com/sdtechdev/spree-jiffyshirts/pull/33466 , https://github.com/sdtechdev/spree-jiffyshirts/pull/33581
        // PDP: https://github.com/sdtechdev/spree-jiffyshirts/pull/33379
        // PLP: https://github.com/sdtechdev/spree-jiffyshirts/pull/33623
      }
    },
    sendChangeDeliveryDateMixpanelData({ commit, getters }) {
      // Use the local getter directly instead of going through rootGetters
      const changeDeliveryDateMixpanelData = getters.changeDeliveryDateMixpanelData || {};
      const isDataComplete = Object.values(changeDeliveryDateMixpanelData).every(
        value => value !== null
      );

      // When the data is gathered, fire the event
      if (getters.deliveryDatesChanged && isDataComplete) {
        track("delivery_dates_changed", changeDeliveryDateMixpanelData);
        // Commit initial state to start gathering the data again
        commit("setChangeDeliveryDateMixpanelData", initialState.changeDeliveryDateMixpanelData);
        commit("setChangeCartItemStartTime", 0);
      }
    },
    startAssignmentStuckTracking({ commit, state, dispatch }) {
      // Clear any existing timers first
      state.assignmentStuckTimers.forEach(timer => clearTimeout(timer));

      const trackingIntervals = [60, 90, 120, 180, 240, 300]; // seconds
      const startTime = Date.now();

      const timers = trackingIntervals.map(seconds =>
        setTimeout(() => {
          track("cart_assignment_stuck", {
            wait_time: seconds
          });
        }, seconds * 1000)
      );

      commit("setAssignmentStuckTimers", timers);
      commit("setAssignmentTrackingStartTime", startTime);

      // Set up cleanup on page unload
      const handleBeforeUnload = () => {
        dispatch("stopAssignmentStuckTracking");
      };

      // Remove existing listener if it exists
      if (window.assignmentStuckCleanup) {
        window.removeEventListener("beforeunload", window.assignmentStuckCleanup);
      }

      window.addEventListener("beforeunload", handleBeforeUnload);
      window.assignmentStuckCleanup = handleBeforeUnload;
    },
    stopAssignmentStuckTracking({ commit, state }) {
      // Clear all timers
      state.assignmentStuckTimers.forEach(timer => clearTimeout(timer));
      commit("setAssignmentStuckTimers", []);
      commit("setAssignmentTrackingStartTime", null);

      // Remove beforeunload listener if it exists
      if (window.assignmentStuckCleanup) {
        window.removeEventListener("beforeunload", window.assignmentStuckCleanup);
        window.assignmentStuckCleanup = null;
      }
    }
  },

  getters: {
    changeCartItemStartTime: state => state.changeCartItemStartTime,
    changeDeliveryDateMixpanelData: (state, getters, rootGetters) => {
      // 'state' here is shipping module's local state.
      // 'state.changeDeliveryDateMixpanelData' is the raw object.
      const rawData = state.changeDeliveryDateMixpanelData || {};
      const {
        earliestTimeToGetDate = null,
        earliestTimeToRenderDate = null,
        cartTimeToGetDate = null,
        cartTimeToRenderDate = null
      } = rawData;
      return {
        ...objNamesToSnakeCase(rawData),
        page: rootGetters.isMcuCart ? "mcu-cart" : "cart",
        earliest_time_to_get_date:
          earliestTimeToGetDate !== null ? Math.floor(earliestTimeToGetDate) : null,
        earliest_time_to_render_date:
          earliestTimeToRenderDate !== null ? Math.floor(earliestTimeToRenderDate) : null,
        cart_time_to_get_date: cartTimeToGetDate !== null ? Math.floor(cartTimeToGetDate) : null,
        cart_time_to_render_date:
          cartTimeToRenderDate !== null ? Math.floor(cartTimeToRenderDate) : null,
        zipcode: rootGetters.guestZipcode || rootGetters.addressSelected?.zipcode,
        reason: getters.isLongWait ? "long_waiting" : "cart_update",
        version: 1,
        shipments: state.shipments?.groupIds?.length || 0
      };
    },
    deliveryDates: state => state.delivery.deliveryDates,
    deliveryDatesMixed: state => state.delivery.deliveryDates.mixed,
    deliveryDatesMixedFastest: state => state.delivery.deliveryDates.mixedFastest,
    deliveryLoading: state => state.deliveryLoadingCounter > 0,
    initialDeliveryDateMixpanelData: state => state.initialDeliveryDateMixpanelData,
    shipments: state => state.shipments,
    selectedShipments: state => {
      if (!state.shipments || !state.shipments.orders) {
        return {};
      }

      return Object.fromEntries(
        Object.values(state.shipments.orders).map(order => [
          order.id,
          order.options.find(o => o.selected)
        ])
      );
    },
    shipMethodChangeGroupId: state => state.shipMethodChangeGroupId,
    shippingInfo: state => state.shippingInfo,
    shippingTexts: state => state.shippingTexts,
    deliveryNotificationObject: state => state.deliveryNotification,
    sortedShipments: state => state.delivery.sortedShipments,
    pegasusShipping: state => id => state.subOrdersFreeShipping[id],
    only59FreeShippingThresholds: state =>
      !Object.values(state.subOrdersFreeShipping).find(
        shipping => shipping.freeShippingThresholdValue !== 59
      ),
    only199FreeShippingThresholds: state =>
      !Object.values(state.subOrdersFreeShipping).find(
        shipping => shipping.freeShippingThresholdValue !== 199
      ),
    lineItemDeliveryDates: (state, getters) => (groupId, id) => {
      const shipment = getters.selectedShipments[groupId];
      if (!shipment) return [];
      const category = camelize(getters.selectedShipments[groupId].category);
      return state.delivery.subOrdersDeliveryDates?.[category]?.[id] ?? [];
    },
    compositionGroupedDates: state => state.delivery.compositionGroupedDates || {},
    hasSameOrNextDayDelivery: (state, getters) =>
      ["sameDay", "nextDay"].some(item => Boolean(getters.deliveryDates[item])),
    deliveryTimeleft: (state, getters) => {
      const selectedShipment = Object.values(getters.selectedShipments)[0];
      if (!selectedShipment) return null;
      return selectedShipment ? selectedShipment.validUntilTime : null;
    },
    minimumDeliveryTimeleft: (state, getters) => {
      if (!state.shipments || !state.shipments.orders) {
        return getters.deliveryTimeleft;
      }

      const [minTimeleft] = Object.values(state.shipments.orders)
        .flatMap(order => order.options.map(o => o.validUntilTime))
        .filter(Boolean)
        .sort((a, b) => new Date(a) - new Date(b));
      return minTimeleft || getters.deliveryTimeleft;
    },
    firstAndLastDeliveryDates: (state, getters) => (groupId, id) => {
      const selectedDeliveryOption = getters.deliveryOptions(groupId).find(o => o.selected);
      if (!selectedDeliveryOption) return 0;

      const selectedDeliverCategory = camelize(selectedDeliveryOption.category);
      const categoryDeliveryDates = state.delivery.subOrdersDeliveryDates[selectedDeliverCategory];
      if (!categoryDeliveryDates) return 0;

      const delivery = categoryDeliveryDates[id];
      if (!delivery) return 0;

      const dates = delivery.dates
        .flatMap(el => [el.deliveryDate, el.paddedDeliveryDate])
        .filter(el => el)
        .sort();
      const firstDate = dates[0];
      const lastDate = dates[dates.length - 1];
      return [new Date(firstDate).getTime(), new Date(lastDate).getTime()];
    },
    changedDeliveryDates: state => state.delivery.changedDeliveryDates,
    deliveryDatesChanged: state => state.delivery.deliveryDatesChanged,
    assignmentUid: state => state.delivery.assignmentUid,
    isLongWait: state => state.delivery.isLongWait,
    orderShipmentPrice: (state, getters) => {
      const { selectedShipments } = getters;
      if (!selectedShipments || Object.keys(selectedShipments).length === 0) return 0;

      return Object.values(selectedShipments).reduce((sum, shipment) => sum + shipment.fee, 0);
    },
    subOrderShipmentPrice: (state, getters) => id => getters.selectedShipments[id]?.fee,
    subOrderSurcharge: (state, getters) => id => getters.selectedShipments[id]?.surcharge,
    subOrderOption: (state, getters) => {
      const { shipments } = getters;
      if (!shipments || !shipments.orders) {
        return {};
      }

      return Object.values(shipments.orders).reduce(
        (subOrders, subOrder) => ({
          ...subOrders,
          [subOrder.id]: Object.values(subOrder.options).reduce((options, option) => {
            const {
              title,
              deliveryDate,
              actualTransitDays,
              calendarTransitDays,
              shippingMethod: shippingProvider,
              surcharge,
              cost
            } = option;
            return {
              ...options,
              [camelize(option.category)]: {
                title,
                deliveryDate,
                actualTransitDays,
                calendarTransitDays,
                shippingProvider,
                surcharge,
                cost
              }
            };
          }, {})
        }),
        {}
      );
    },
    deliveryOptions: state => id =>
      state.shipments.orders && state.shipments.orders[id]
        ? state.shipments.orders[id].options
        : [],
    saleShippingPrices: state => state.saleImprovedShippingPriceDetails,
    showBlanksFreeShippingBanner: state => state.showBlanksFreeShippingBanner,
    isEligibleForBlanksFreeShipping: state => state.isEligibleForBlanksFreeShipping,
    deliveryTotal: (state, getters) => {
      const { selectedShipments } = getters;
      if (!selectedShipments || Object.keys(selectedShipments).length === 0) return 0;

      const reducer = (sum, { fee }) => fee + sum;
      return Object.values(selectedShipments).reduce(reducer, 0);
    },
    surchargeTotal: (state, getters) => {
      const { selectedShipments } = getters;
      if (!selectedShipments || Object.keys(selectedShipments).length === 0) return 0;

      // Surcharge is charged once per order, not per shipment
      // Find the first shipment with surcharge and return that value
      const shipmentWithSurcharge = Object.values(selectedShipments).find(
        ({ surcharge }) => surcharge > 0
      );
      return shipmentWithSurcharge ? shipmentWithSurcharge.surcharge : 0;
    },
    selectedShipmentsProxy: (state, getters) => {
      const { selectedShipments } = getters;
      if (!selectedShipments || Object.keys(selectedShipments).length === 0) {
        return {
          summary: {
            fee: 0,
            strikedShipPrice: 0,
            surcharge: 0
          }
        };
      }

      // Surcharge is charged once per order, not per shipment
      const shipmentWithSurcharge = Object.values(selectedShipments).find(
        ({ surcharge }) => surcharge > 0
      );
      const orderSurcharge = shipmentWithSurcharge ? shipmentWithSurcharge.surcharge : 0;

      return {
        summary: {
          fee: Object.values(selectedShipments).reduce((prevFee, { fee }) => prevFee + fee, 0),
          strikedShipPrice: Object.values(selectedShipments).reduce(
            (prevPrice, { strikedShipPrice }) => prevPrice + strikedShipPrice,
            0
          ),
          surcharge: orderSurcharge
        }
      };
    },
    shipmentsOrdersProxy: (state, getters) => {
      const { shipments } = getters;
      if (!shipments || !shipments.orders) {
        return {};
      }

      const { orders } = shipments;

      if (Object.values(orders).length) {
        return {
          summary: {
            id: "summary",
            title: "Shipping"
          }
        };
      }

      return orders;
    },
    shippingDiscount: (state, getters) => {
      const { shipmentsOrdersProxy, selectedShipmentsProxy } = getters;
      return Object.values(shipmentsOrdersProxy).reduce((sum, shipOrderProxy) => {
        // Ensure selectedShipmentsProxy[shipOrderProxy.id] exists before destructuring
        const selectedShipmentData = selectedShipmentsProxy[shipOrderProxy.id];
        if (selectedShipmentData) {
          const { strikedShipPrice, fee } = selectedShipmentData;
          const diff = strikedShipPrice - fee;
          if (diff > 0) {
            sum += diff;
          }
        }
        return sum;
      }, 0);
    },
    freeShipping:
      (state, getters) =>
      (id = 0) => {
        const { pegasusShipping } = getters;
        const shipping = pegasusShipping(id) || {};

        // For blanks group (id = 0), also check blanks free shipping eligibility
        if (String(id) === "0") {
          const pegasusFreeShipping = !!shipping.freeShipping;
          const blanksFreeShipping = state.isEligibleForBlanksFreeShipping;
          return pegasusFreeShipping || blanksFreeShipping;
        }

        return !!shipping.freeShipping;
      },
    mcuOnlySelectedOption: (state, getters, rootState, rootGetters) => {
      let option;
      const { selectedShipments } = getters;
      const { fgWithNonFastestOption } = rootGetters;
      // Prioritize selected option on fgWithNonFastestOption
      option = selectedShipments[fgWithNonFastestOption[0]];
      if (!option) {
        [option] = Object.values(selectedShipments);
      }
      return option;
    },
    sortedSelectedShipments: (state, getters) => {
      const { selectedShipments, sortedShipments } = getters;
      return sortedShipments.filter(shipment => {
        const { groupId, category } = shipment;

        return !!Object.entries(selectedShipments).find(
          ([curGroupId, opt]) => groupId === +curGroupId && category === opt.category
        );
      });
    },
    groupedSortedShipments: (state, getters) => {
      const { sortedShipments } = getters;
      return Object.values(sortedShipments).reduce(
        (res, shipment) => ({
          ...res,
          [shipment.groupId]: [...(res[shipment.groupId] || []), shipment]
        }),
        {}
      );
    },
    // eslint-disable-next-line max-params
    deliveryNotification: (state, getters, rootState, rootGetters) => {
      const shippingDeliveryNotif = getters.deliveryNotificationObject;
      if (!shippingDeliveryNotif) return null;

      const { category } = rootGetters.isMcuCart
        ? getters.sortedSelectedShipments[0] || {}
        : getters.selectedShipments[0] || {};

      if (category && shippingDeliveryNotif[camelize(category)]) {
        return shippingDeliveryNotif[camelize(category)];
      }

      return shippingDeliveryNotif.mixedFastest || shippingDeliveryNotif.mixed;
    },
    freeShippingCouponDiscount: (state, getters, rootState, rootGetters) => {
      const { couponCode } = rootGetters;
      if (!couponCode || !couponCode.freeShipping) return 0;

      const { subOrderShipmentPrice, selectedShipments } = getters;
      if (couponCode.freeShippingPromotionType === "single") {
        return subOrderShipmentPrice(couponCode.singleFreeShippingSubOrderId);
      }

      return Object.values(selectedShipments).reduce(
        (total, shipment) => total + (shipment.free ? 0 : shipment.strikedShipPrice),
        0
      );
    },
    automaticPromotionShippingDiscount: (state, getters) => {
      const { invalidDeliveryState, freeShippingCouponDiscount, selectedShipments } = getters;
      if (invalidDeliveryState || freeShippingCouponDiscount) return 0;

      return Object.values(selectedShipments).reduce(
        (total, shipment) => total + parseFloat(shipment.automaticPromotionShippingDiscount || 0),
        0
      );
    },
    shippingTotal: (state, getters) => {
      const {
        deliveryTotal,
        surchargeTotal,
        invalidDeliveryState,
        freeShippingCouponDiscount,
        automaticPromotionShippingDiscount
      } = getters;
      let shippingTotal = 0;

      if (!invalidDeliveryState) {
        shippingTotal += parseFloat(deliveryTotal);
        shippingTotal += parseFloat(surchargeTotal);
        if (freeShippingCouponDiscount) shippingTotal -= parseFloat(freeShippingCouponDiscount);

        if (automaticPromotionShippingDiscount > 0) {
          shippingTotal -= automaticPromotionShippingDiscount;
        }
      }

      return shippingTotal;
    },
    mcuPaypalShippingOptions: (state, getters, rootState, rootGetters) => {
      const mcuShipmentsOnly = !getters.selectedShipments[0];

      if (mcuShipmentsOnly) {
        return Object.keys(getters.deliveryDates).map(category => {
          const mcuShipments = getters.deliveryDates[category];
          const shippingOptionsDates = [
            ...new Set(mcuShipments.map(({ formattedDeliveryDate }) => formattedDeliveryDate))
          ];
          const deliveryDates = [
            ...new Set([
              shippingOptionsDates[0],
              shippingOptionsDates[shippingOptionsDates.length - 1]
            ])
          ];

          let label = `${getters.shipments.titles[camelize(category)] || "Shipping"} - `;
          label +=
            deliveryDates.length > 1
              ? deliveryDates.map(date => date.split(", ")[1]).join(" - ")
              : deliveryDates[0];

          const cost = rootGetters.couponCode.freeShipping ? 0 : getters.mcuShippingCost(category);

          return {
            type: "SHIPPING",
            id: category,
            label,
            selected: category === (getters.mcuOnlySelectedOption?.category || "fastest"),
            amount: {
              value: cost.toFixed(2),
              currency_code: rootGetters.currencyCode
            }
          };
        });
      }

      if (rootGetters.couponCode.freeShippingPromotionType === "all") {
        return getters.standardPaypalShippingOptions;
      }

      // PayPal allows 1 selected shipping option for multi-shipments,
      // aggregate shipments cost per shipping option
      return getters.standardPaypalShippingOptions.map(option => ({
        ...option,
        amount: {
          value: (parseFloat(option.amount.value) + getters.mcuShippingCost(option.id)).toFixed(2),
          currency_code: option.amount.currency_code
        }
      }));
    },
    standardPaypalShippingOptions: (state, getters, rootState, rootGetters) => {
      const shippingOptions = getters.standardShippingOptions.filter(o => o);
      return shippingOptions.map(option => {
        const cost = rootGetters.couponCode.freeShipping || option.free ? 0 : option.cost;

        // build label
        const shippingOptionsDates = [
          ...new Set(
            getters
              .sortedStandardShippingOptions(option)
              .map(
                shippingOption =>
                  shippingOption?.formattedDeliveryDate || shippingOption?.formattedArrivalDate
              )
          )
        ];
        const deliveryDates = [
          ...new Set([
            shippingOptionsDates[0],
            shippingOptionsDates[shippingOptionsDates.length - 1]
          ])
        ];
        let label = `${option.title || "Shipping"} - `;
        label += deliveryDates.length
          ? deliveryDates.map(date => date.split(", ")[1]).join(" - ")
          : deliveryDates[0];

        return {
          type: "SHIPPING",
          id: option.category,
          label,
          selected: option.selected,
          amount: {
            value: cost.toFixed(2),
            currency_code: rootGetters.currencyCode
          }
        };
      });
    },
    standardShipmentOption: (state, getters) => category =>
      getters.deliveryOptions(0) &&
      getters.deliveryOptions(0).find(option => camelize(option.category) === camelize(category)),
    standardShippingOptions: (state, getters) =>
      Object.keys(getters.deliveryDates).map(category => getters.standardShipmentOption(category)),
    sortedStandardShippingOptions: (state, getters, rootState, rootGetters) => option => {
      const { 0: _, ...mcuSelectedShipments } = getters.selectedShipments;
      const mcuShipments = Object.values(mcuSelectedShipments)
        .flat()
        .map(({ shipments }) => shipments)
        .flat();
      const standardShipments =
        getters.deliveryDates[rootGetters.isUs ? camelize(option.category) : "caFastest"];
      const shippingOptions = [...(standardShipments || []), ...(mcuShipments || [])];
      return shippingOptions.sort(
        (a, b) =>
          new Date(a?.date || a?.deprecated?.date) - new Date(b?.date || b?.deprecated?.date)
      );
    },
    mcuShippingCost: (state, getters, rootState, rootGetters) => category => {
      const { 0: _, ...mcuShipments } = getters.shipments.orders;
      const total = Object.values(mcuShipments)
        .flat()
        .reduce((res, shipment) => {
          // Find the MCU shipment same as the selected shipping category
          let option = shipment.options.find(o => camelize(o.category) === category);

          // All shipments contain the 'fastest' option.
          // Entering this block means the MCU shipment doesn't offer the selected shipping
          // category. And the customer is most probably considering cost over delivery dates.
          // Hence, we offer the cheapest MCU shipping option possible.
          if (!option) {
            const shipmentOptions = shipment.options;
            option = shipmentOptions[shipmentOptions.length - 1];
          }

          const cost = rootGetters.couponCode.freeShipping || option.free ? 0 : option.cost;
          return res + cost + (option.surcharge || 0);
        }, 0);

      return total;
    },
    textForCategory: (state, getters) => category => {
      const { shippingTexts } = getters;
      if (shippingTexts && shippingTexts[camelize(category)]) {
        return shippingTexts[camelize(category)].content;
      }
      return ""; // Default or error handling
    },
    madeShippingRule: (state, getters, rootState) =>
      rootState.cartState.cartConfig.madeShippingRule,
    dtfShippingRule: (state, getters, rootState) => rootState.cartState.cartConfig.dtfShippingRule,
    imageServiceShippingRule: (state, getters, rootState) =>
      rootState.cartState.cartConfig.imageServiceShippingRule,
    spangleShippingRule: (state, getters, rootState) =>
      rootState.cartState.cartConfig.spangleShippingRule,
    isStandardDeliveryInfoVisible: (state, getters, rootState) =>
      rootState.cartState.cartConfig.isStandardDeliveryInfoVisible,
    deliveryWarehouses: (state, getters, rootState) =>
      rootState.cartState.cartConfig.deliveryWarehouses,
    invalidDeliveryState: (state, getters, rootState, rootGetters) => {
      const { locationLoaded, addressSelected } = rootGetters;
      // case for autoh user with adress
      // check if user's address is valid
      if (addressSelected) return !addressSelected.stateValid;
      // case for typed zip code
      // check if the state is allowed
      return locationLoaded && !rootState.cartState.location.stateValid;
    },
    orderGroupsSortOrder: (state, getters) => {
      const transfers = getters.dtfShippingRule;
      const spangles = getters.spangleShippingRule;
      const blanks = "0";

      const order = [transfers, spangles, blanks];
      return order;
    },
    stripeShippingOptions: (state, getters, rootState, rootGetters) => {
      const mcuStripePayload = rootGetters["payments/mcuStripePayload"];
      const standardStripePayload = rootGetters["payments/standardStripePayload"];
      const options = rootGetters.isMcuCart ? mcuStripePayload : standardStripePayload;
      return options.filter(option => option && option.identifier);
    },
    stripeShippingRates: (state, getters, rootState, rootGetters) => {
      const { storeCurrency } = rootGetters;
      const shipOptions = getters.stripeShippingOptions.map(option => {
        let label = "";
        const surcharge = option.surcharge || 0;
        if (option.amount - surcharge > 0) {
          const baseAmount = parseFloat(option.amount - surcharge);
          label = `${option.label} ${storeCurrency}${baseAmount.toFixed(2)} Flat Rate`;
        } else {
          label = `FREE ${option.label} Shipping`;
        }
        if (surcharge > 0) {
          label += ` + ${storeCurrency}${surcharge} Fuel Surcharge`;
        }

        return {
          id: option.identifier,
          displayName: label,
          amount: Math.round(option.amount * 100),
          deliveryEstimate: option.deliveryEstimate
        };
      });

      const selectedShippingOption =
        getters.stripeShippingOptions.find(option => option.selected)?.identifier || "standard";

      return shipOptions.sort((a, _b) => {
        if (a.id === selectedShippingOption) {
          return -1;
        }
        return 1;
      });
    },
    paypalShippingOptions: (state, getters, rootState, rootGetters) => {
      const { isMcuCart } = rootGetters;
      const { mcuPaypalShippingOptions, standardPaypalShippingOptions } = getters;

      return isMcuCart ? mcuPaypalShippingOptions : standardPaypalShippingOptions;
    },
    debugInfo: (state, getters, rootState, rootGetters) => {
      const { isMcuCart, couponCode } = rootGetters;
      const {
        paypalShippingOptions,
        standardShippingOptions,
        deliveryDates,
        selectedShipments,
        shipments
      } = getters;

      return {
        shipments_orders: shipments.orders,
        couponCode,
        deliveryDates,
        isMcuCart,
        paypalShippingOptions,
        selectedShipments,
        standardShippingOptions
      };
    }
  }
};

export default shippingModule;
